import type {
  AccessResponse,
  ApiErrorBody,
  ApiErrorCode,
  CheckoutRequest,
  CheckoutResponse,
  CompleteTransferRequest,
  CompleteTransferResponse,
  DeleteTransferResponse,
  ExtendTransferRequest,
  ExtendTransferResponse,
  InitTransferRequest,
  InitTransferResponse,
  OrderStatusResponse,
  PartUrlsResponse,
  ReportRequest,
  TransferStatusResponse,
} from "@limeshare/shared";
import { API_BASE } from "./config";

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | "NETWORK",
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

interface RequestInitEx extends RequestInit {
  token?: string;
}

async function request<T>(path: string, init: RequestInitEx = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (init.token) headers.set("authorization", `Bearer ${init.token}`);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: "omit", mode: "cors" });
  } catch (e) {
    throw new ApiClientError(0, "NETWORK", `Cannot reach the API at ${API_BASE}: ${e instanceof Error ? e.message : String(e)}`);
  }
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    const body = json as ApiErrorBody | null;
    const code = body?.error?.code ?? "INTERNAL";
    const message = body?.error?.message ?? `HTTP ${res.status}`;
    throw new ApiClientError(res.status, code, message, body?.error?.details);
  }
  return json as T;
}

export const api = {
  initTransfer: (body: InitTransferRequest) =>
    request<InitTransferResponse>("/v1/transfers", { method: "POST", body: JSON.stringify(body) }),

  partUrls: (transferId: string, fileId: string, from: number, count: number, token: string) =>
    request<PartUrlsResponse>(`/v1/transfers/${transferId}/files/${fileId}/parts?from=${from}&count=${count}`, { token }),

  complete: (transferId: string, token: string, body: CompleteTransferRequest) =>
    request<CompleteTransferResponse>(`/v1/transfers/${transferId}/complete`, {
      method: "POST",
      token,
      body: JSON.stringify(body),
    }),

  access: (transferId: string, token: string, turnstileToken?: string) =>
    request<AccessResponse>(`/v1/transfers/${transferId}/access`, {
      method: "POST",
      token,
      body: JSON.stringify(turnstileToken ? { turnstileToken } : {}),
    }),

  completeDownload: (transferId: string, token: string) =>
    request<{ ok: true }>(`/v1/transfers/${transferId}/complete-download`, { method: "POST", token, body: "{}" }),

  report: (transferId: string, body: ReportRequest) =>
    request<{ ok: true; caseId: string }>(`/v1/transfers/${transferId}/report`, { method: "POST", body: JSON.stringify(body) }),

  status: (transferId: string, token: string) =>
    request<TransferStatusResponse>(`/v1/transfers/${transferId}/status`, { token }),

  remove: (transferId: string, token: string) =>
    request<DeleteTransferResponse>(`/v1/transfers/${transferId}`, { method: "DELETE", token }),

  extend: (transferId: string, body: ExtendTransferRequest) =>
    request<ExtendTransferResponse>(`/v1/transfers/${transferId}/extend`, { method: "POST", body: JSON.stringify(body) }),

  checkout: (body: CheckoutRequest) =>
    request<CheckoutResponse>("/v1/billing/checkout", { method: "POST", body: JSON.stringify(body) }),

  orderStatus: (idOrCode: string) =>
    request<OrderStatusResponse>(`/v1/billing/orders/${encodeURIComponent(idOrCode)}`),

  getComments: (transferId: string, token: string) =>
    request<{
      comments: { id: number; fileId: string | null; timestampSec: number | null; authorName: string; commentText: string; createdAt: number }[];
    }>(`/v1/transfers/${transferId}/comments`, { token }),

  addComment: (
    transferId: string,
    token: string,
    body: { fileId?: string; timestampSec?: number | null; authorName: string; commentText: string },
  ) =>
    request<{
      comment: { id: number; fileId: string | null; timestampSec: number | null; authorName: string; commentText: string; createdAt: number };
    }>(`/v1/transfers/${transferId}/comments`, { method: "POST", token, body: JSON.stringify(body) }),

  createRequestPortal: (body: { title: string; description?: string; customSlug?: string; ownerToken: string }) =>
    request<{ slug: string; title: string; expiresAt: number }>("/v1/requests", { method: "POST", body: JSON.stringify(body) }),

  getRequestPortal: (slug: string, ownerToken?: string) =>
    request<{
      slug: string;
      title: string;
      description: string | null;
      createdAt: number;
      expiresAt: number;
      isOwner: boolean;
      submissions: { id: number; transferId: string; shareLink: string; senderName: string | null; note: string | null; createdAt: number }[];
    }>(`/v1/requests/${encodeURIComponent(slug)}`, ownerToken ? { token: ownerToken } : {}),

  submitToPortal: (slug: string, body: { transferId: string; shareLink: string; senderName?: string; note?: string }) =>
    request<{ ok: true; submittedAt: number }>(`/v1/requests/${encodeURIComponent(slug)}/submit`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
